import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { decryptData } from "../../utils/encrypt";
import {
  PACKING_TYPE_LIST_ENDPOINT,
  SALES_ORDER_DESTINATIONS,
  SALES_ORDER_PLAN_UPDATE,
  SALES_ORDER_PLAN_DELETE,      // <-- ⬅️ ADD THIS
} from "../../config/api";

function EditPlanningDetail() {
  const navigate = useNavigate();
  const { planningId } = useParams();
  const { state } = useLocation();
  const passedPlan = state?.plan; // may be undefined if opened directly

  // ---------- helpers ----------
  const toYMD = (val) => {
    if (!val) return "";
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d)) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // ---------- Packing Types (string values) ----------
  const [packingOptions, setPackingOptions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(PACKING_TYPE_LIST_ENDPOINT);
        const data = await res.json(); // expect [{id, name}] or [{id, packing_type}]
        const opts = Array.isArray(data)
          ? data.map((p) => p.name ?? p.packing_type).filter(Boolean)
          : [];
        setPackingOptions([...new Set(opts)]); // de-dup
      } catch (e) {
        console.warn("Packing types fetch failed:", e);
        setPackingOptions([]);
      }
    })();
  }, []);

  // ---------- Initial form values ----------
  const initial = useMemo(() => {
    return {
      planning_id:
        passedPlan?.planning_id ??
        passedPlan?.plan_id ??
        passedPlan?.id ??
        planningId,
      customer_id:
        passedPlan?.customer_id ??
        passedPlan?.account_id ??
        passedPlan?.accountid ??
        "",
      customer_name: passedPlan?.customer_name ?? passedPlan?.customer ?? "",
      material_name: passedPlan?.material_name ?? "",
      packing_type: passedPlan?.packing_style ?? passedPlan?.packing_type ?? "",
      planned_qty:
        passedPlan?.planned_qty === 0
          ? "0"
          : (passedPlan?.planned_qty ?? ""),
      delivery_date: toYMD(passedPlan?.delivery_date) || "",
      destination:
        passedPlan?.destination_point ?? passedPlan?.destination ?? "",
      priority: passedPlan?.priority ?? "",
      remark: passedPlan?.remark ?? "",
    };
  }, [passedPlan, planningId]);

  const [form, setForm] = useState(initial);
  useEffect(() => {
    setForm(initial);
  }, [initial]);

  // ---------- Destinations by customer_id ----------
  const [destinationList, setDestinationList] = useState([]);
  const [destLoading, setDestLoading] = useState(false);

  const fetchDestinationsByCustomer = async (customer_id) => {
    if (!customer_id) {
      setDestinationList([]);
      return;
    }
    setDestLoading(true);
    try {
      const destRes = await fetch(SALES_ORDER_DESTINATIONS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id }),
      });
      const destData = await destRes.json();

      // accept array shapes
      const raw = Array.isArray(destData)
        ? destData
        : Array.isArray(destData?.records)
        ? destData.records
        : Array.isArray(destData?.data)
        ? destData.data
        : [];

      setDestinationList(raw);
    } catch (err) {
      console.error("Error fetching destinations:", err);
      setDestinationList([]);
    } finally {
      setDestLoading(false);
    }
  };

  useEffect(() => {
    fetchDestinationsByCustomer(form.customer_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_id]);

  // Normalize destination options to string labels; ensure current value is present
  const destinationOptions = useMemo(() => {
    const toLabel = (d) =>
      d?.destination_city ??
      d?.destination ??
      d?.city ??
      d?.name ??
      d?.label ??
      (typeof d === "string" ? d : "");

    const labels = new Set();
    const opts = [];

    destinationList.forEach((d) => {
      const label = String(toLabel(d)).trim();
      if (label && !labels.has(label)) {
        labels.add(label);
        opts.push({ key: d?.id ?? label, label });
      }
    });

    if (form.destination) {
      const current = String(form.destination).trim();
      if (current && !labels.has(current)) {
        opts.unshift({ key: current, label: current });
      }
    }

    return opts;
  }, [destinationList, form.destination]);

  // ---------- Form handlers ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSave = async () => {
    try {
      const encrypted = localStorage.getItem("suyog_user");
      let user_id = null;
      if (encrypted) {
        try {
          const parsed = decryptData(encrypted);
          user_id = parsed?.user_id || null;
        } catch (e) {
          console.error("decryptData failed; falling back to JSON.parse");
          try {
            const parsed = JSON.parse(encrypted);
            user_id = parsed?.user_id || null;
          } catch {}
        }
      }
      if (!user_id) {
        alert("Missing user session.");
        return;
      }

      // Build payload. Keep strings; backend will skip qty if empty.
      const payload = {
        planning_id: form.planning_id,
        user_id,
        packing_type: form.packing_type, // string (mapped to packing_style on server)
        planned_qty: form.planned_qty,   // may be '', PHP will skip
        delivery_date: form.delivery_date,
        destination: form.destination,
        priority: form.priority,
        remark: form.remark || "",
      };

      const res = await fetch(SALES_ORDER_PLAN_UPDATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (res.ok && json.success) {
        alert("Planning updated!");
        navigate(-1); // go back to the list page
      } else {
        console.error("Update failed:", json);
        alert(json.error || "Failed to update planning");
      }
    } catch (e) {
      console.error(e);
      alert("Server error while updating planning");
    }
  };

  // ---------- Delete ----------
  const [deleting, setDeleting] = useState(false);
  const onDelete = async () => {
    if (!form.planning_id) {
      alert("Missing planning id");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this planning?")) return;

    try {
      setDeleting(true);

      const encrypted = localStorage.getItem("suyog_user");
      let user_id = null;
      if (encrypted) {
        try {
          const parsed = decryptData(encrypted);
          user_id = parsed?.user_id || null;
        } catch (e) {
          try {
            const parsed = JSON.parse(encrypted);
            user_id = parsed?.user_id || null;
          } catch {}
        }
      }
      if (!user_id) {
        alert("Missing user session.");
        setDeleting(false);
        return;
      }

      const payload = {
        planning_id: form.planning_id,
        user_id,
      };

      const res = await fetch(SALES_ORDER_PLAN_DELETE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (res.ok && json.success) {
        alert("Planning deleted.");
        navigate(-1);
      } else {
        console.error("Delete failed:", json);
        alert(json.error || "Failed to delete planning");
      }
    } catch (e) {
      console.error(e);
      alert("Server error while deleting planning");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>✏️ Edit Planning #{form.planning_id}</h4>
          <div className="d-flex gap-2">
            <button className="btn btn-light" onClick={() => navigate(-1)}>
              ← Back
            </button>
            <button
              className="btn btn-outline-danger"
              onClick={onDelete}
              disabled={deleting}
              title="Delete this planning"
            >
              {deleting ? "Deleting..." : "🗑 Delete"}
            </button>
          </div>
        </div>

        {/* Summary (read-only) */}
        <div className="card mb-3">
          <div className="card-body p-2">
            <div className="row g-2 small">
              <div className="col-md-3">
                <strong>Customer:</strong> {form.customer_name || "—"}
              </div>
              <div className="col-md-3">
                <strong>Customer ID:</strong> {form.customer_id || "—"}
              </div>
              <div className="col-md-3">
                <strong>Material:</strong> {form.material_name || "—"}
              </div>
              <div className="col-md-3">
                <strong>Current Packing:</strong>{" "}
                {(passedPlan?.packing_style ?? passedPlan?.packing_type) || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Edit form */}
        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              {/* Packing Type */}
              <div className="col-md-4">
                <label className="form-label">
                  Packing Type<span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="packing_type"
                  value={form.packing_type}
                  onChange={handleChange}
                  required
                >
                  <option value="">
                    {packingOptions.length ? "-- Select --" : "Loading..."}
                  </option>
                  {packingOptions.map((txt) => (
                    <option key={txt} value={txt}>
                      {txt}
                    </option>
                  ))}
                  {packingOptions.length === 0 && (
                    <>
                      <option value="25 Kgs Bags">25 Kgs Bags</option>
                      <option value="50 Kgs Bag">50 Kgs Bag</option>
                      <option value="Loose Bulker">Loose Bulker</option>
                    </>
                  )}
                </select>
              </div>

              {/* Planned Qty */}
              <div className="col-md-4">
                <label className="form-label">
                  Planned Qty<span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="form-control"
                  name="planned_qty"
                  value={form.planned_qty}
                  onChange={handleChange}
                  placeholder="e.g. 12.500"
                />
                <small className="text-muted">
                  Leave blank if you only want to change date/destination/priority.
                </small>
              </div>

              {/* Delivery Date */}
              <div className="col-md-4">
                <label className="form-label">
                  Delivery Date<span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="delivery_date"
                  value={form.delivery_date}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Destination (dropdown by customer_id) */}
              <div className="col-md-6">
                <label className="form-label">
                  Destination<span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="destination"
                  value={form.destination}
                  onChange={handleChange}
                  required
                  disabled={destLoading && !destinationOptions.length}
                >
                  <option value="">
                    {destLoading && !destinationOptions.length
                      ? "Loading..."
                      : "-- Select Destination --"}
                  </option>
                  {destinationOptions.map((d) => (
                    <option key={d.key} value={d.label}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div className="col-md-3">
                <label className="form-label">
                  Priority<span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="priority"
                  value={form.priority}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Priority</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="On Hold">On Hold</option>
                </select>
              </div>

              {/* Remark */}
              <div className="col-md-3">
                <label className="form-label">Remark</label>
                <input
                  type="text"
                  className="form-control"
                  name="remark"
                  value={form.remark}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end mt-3">
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={onSave}>
                💾 Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default EditPlanningDetail;
